import { defineStore } from 'pinia';
import { globalSearch } from '@/api/searchApi.js';

const TYPES = ['customers', 'facilities', 'contracts', 'works', 'contacts'];

export const useSearchStore = defineStore('globalSearch', {
    state: () => ({
        query: '',
        includeExtended: false,
        typesSelected: [...TYPES], // по умолчанию все
        totals: { customers: 0, facilities: 0, contracts: 0, works: 0, contacts: 0 },
        facets: {
            customers: { items: [], total: 0, page: 1, pageSize: 10, loading: false },
            facilities: { items: [], total: 0, page: 1, pageSize: 10, loading: false },
            contracts: { items: [], total: 0, page: 1, pageSize: 10, loading: false },
            works: { items: [], total: 0, page: 1, pageSize: 10, loading: false },
            contacts: { items: [], total: 0, page: 1, pageSize: 10, loading: false },
        },
        loading: false,
        error: null,
    }),

    getters: {
        totalAll: (s) =>
            (s.totals?.customers || 0) +
            (s.totals?.facilities || 0) +
            (s.totals?.contracts || 0) +
            (s.totals?.works || 0) +
            (s.totals?.contacts || 0),
    },

    actions: {
        setQuery(q) {
            this.query = q || '';
            TYPES.forEach((t) => (this.facets[t].page = 1));
        },
        setIncludeExtended(v) {
            this.includeExtended = !!v;
            TYPES.forEach((t) => (this.facets[t].page = 1));
        },
        setTypes(arr) {
            this.typesSelected = Array.isArray(arr) && arr.length ? arr : [...TYPES];
            TYPES.forEach((t) => (this.facets[t].page = 1));
        },
        setPage(type, page) {
            if (!TYPES.includes(type)) return;
            this.facets[type].page = Math.max(1, Number(page || 1));
        },
        setPageSize(type, size) {
            if (!TYPES.includes(type)) return;
            this.facets[type].pageSize = Math.max(1, Number(size || 10));
            this.facets[type].page = 1;
        },

        async fetchAll() {
            if (!this.query || this.query.trim().length < 2) {
                this._clearResults();
                return;
            }
            this.loading = true;
            this.error = null;

            const params = {
                q: this.query,
                include_extended: this.includeExtended || undefined,
                types: this.typesSelected.join(','),
            };
            TYPES.forEach((t) => {
                params[`${t}_page`] = this.facets[t].page;
                params[`${t}_page_size`] = this.facets[t].pageSize;
            });

            try {
                const data = await globalSearch(params);
                this.totals = { ...(data?.totals || {}) };
                const f = data?.facets || {};
                TYPES.forEach((t) => {
                    const facet = f?.[t] || { items: [], total: 0 };
                    this.facets[t].items = Array.isArray(facet.items) ? facet.items : [];
                    this.facets[t].total = Number(facet.total || 0);
                    this.facets[t].loading = false;
                });
            } catch (e) {
                this.error = 'Не удалось выполнить поиск';
            } finally {
                this.loading = false;
            }
        },

        async fetchFacet(type) {
            if (!TYPES.includes(type)) return;
            if (!this.query || this.query.trim().length < 2) return;

            const facet = this.facets[type];
            facet.loading = true;

            const params = {
                q: this.query,
                include_extended: this.includeExtended || undefined,
                types: this.typesSelected.join(','),
            };
            TYPES.forEach((t) => {
                params[`${t}_page`] = this.facets[t].page;
                params[`${t}_page_size`] = this.facets[t].pageSize;
            });

            try {
                const data = await globalSearch(params);
                this.totals = { ...(data?.totals || {}) };
                const f = data?.facets?.[type] || { items: [], total: 0 };
                facet.items = Array.isArray(f.items) ? f.items : [];
                facet.total = Number(f.total || 0);
            } catch (e) {
                this.error = `Не удалось загрузить вкладку ${type}`;
            } finally {
                facet.loading = false;
            }
        },

        _clearResults() {
            this.totals = { customers: 0, facilities: 0, contracts: 0, works: 0, contacts: 0 };
            TYPES.forEach((t) => {
                this.facets[t].items = [];
                this.facets[t].total = 0;
                this.facets[t].page = 1;
            });
        },

        // Публичный метод: очистка результатов и запросной строки
        clearResults() {
            this.query = '';
            this._clearResults();
        },

        // Публичный метод: совместимость с компонентом поиска
        async performSearch() {
            await this.fetchAll();
        }
    },
});